/**
 * A-02 — the top 100 medicines, and where any medicine stands.
 *
 * The ranking (by units, revenue or orders sold in the period) lists the best 100 sellers, each
 * with its move since the previous period. "Find a medicine" searches the whole catalogue; picking
 * one (or a row) opens its analytics: its rank among everything sold — also outside the top 100 —
 * its sales against the previous period, its trend, and how often it couldn't be supplied.
 *
 * The period, the ranking and the medicine shown live in the URL (?days=&sort=&medicine=): links
 * from the dashboard land on them, and Back closes the medicine.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { listMedicines, type Medicine } from "../../api/admin";
import {
  medicineSales,
  topMedicines,
  type MedicineSales,
  type RankedMedicine,
  type TopMedicines as Ranking,
} from "../../api/analytics";
import { describeError } from "../../api/errors";
import { ColumnChart, type ColumnBar } from "../../components/kpi/ColumnChart";
import { ExportButton } from "../../components/ui/ExportButton";
import { MEDICINE_RANK_EXPORT } from "../../lib/kpiExports";
import {
  Card,
  ChangeBadge,
  LoadError,
  RankMoveBadge,
  Segmented,
  ShareBar,
  Skeleton,
  WarningIcon,
} from "../../components/kpi/KpiParts";
import {
  change,
  chunkFromEnd,
  dayLabel,
  formatNumber,
  formatPercent,
  MEDICINE_SORTS,
  parseMedicineSort,
  PERIODS,
  previousLabel,
  rangeLabel,
  rankMove,
  runLabel,
  runLength,
  standing,
  wallClock,
  type MedicineSort,
} from "../../lib/kpi";
import { formatINR, formatINRCompact } from "../../lib/money";
import { useDebouncedValue } from "../../lib/useDebouncedValue";
import { useKpiPeriod } from "../../lib/useKpiPeriod";

const RANKING_ROWS = 100;
const PERIOD_OPTIONS = PERIODS.map((p) => ({ value: p.days, label: p.label }));
const SORT_OPTIONS = MEDICINE_SORTS.map((s) => ({ value: s.value, label: s.label }));
const sortNoun = (sort: MedicineSort) => MEDICINE_SORTS.find((s) => s.value === sort)?.noun ?? sort;

export function TopMedicines() {
  const [days, setDays] = useKpiPeriod();
  const [params, setParams] = useSearchParams();
  const sort = parseMedicineSort(params.get("sort"));
  const medicineId = params.get("medicine");
  const panel = useRef<HTMLDivElement>(null);

  const setParam = (key: string, value: string | null, replace: boolean) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace },
    );
  // Opening a medicine is a step Back undoes; changing the ranking isn't.
  const showMedicine = (id: string) => setParam("medicine", id, false);

  const ranking = useQuery({
    queryKey: ["kpi", "top-medicines", days, sort, RANKING_ROWS],
    queryFn: () => topMedicines(days, sort, RANKING_ROWS),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (medicineId) panel.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [medicineId]);

  return (
    <div className="page">
      <Link to={`/staff/admin/kpi?days=${days}`} className="link inline-flex items-center gap-1 text-sm">
        <span aria-hidden>←</span> Business overview
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h2 className="page-title">Top-selling medicines</h2>
          <p className="page-subtitle">
            {ranking.data
              ? `${rangeLabel(ranking.data.period_start, ranking.data.period_end)} · ranked by ${sortNoun(sort)}, with each rank's move since ${previousLabel(days)}`
              : `Ranked by ${sortNoun(sort)}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Segmented label="Period" options={PERIOD_OPTIONS} value={days} onChange={setDays} />
          <Segmented label="Rank by" options={SORT_OPTIONS} value={sort} onChange={(s) => setParam("sort", s, true)} />
        </div>
      </div>

      <MedicineFinder ranked={ranking.data?.rows ?? []} onPick={showMedicine} />

      <div ref={panel} className="scroll-mt-4">
        {medicineId && (
          <MedicineAnalytics key={medicineId} id={medicineId} days={days} sort={sort} onClose={() => setParam("medicine", null, false)} />
        )}
      </div>

      <RankingCard
        query={ranking}
        sort={sort}
        selected={medicineId}
        onSelect={showMedicine}
      />
    </div>
  );
}

// ------------------------------------------------------------------ find a medicine

function MedicineFinder({ ranked, onPick }: { ranked: RankedMedicine[]; onPick: (id: string) => void }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const term = useDebouncedValue(text.trim(), 250);
  const inputId = useId();
  const listId = useId();
  const found = useQuery({
    queryKey: ["kpi", "medicine-search", term],
    queryFn: () => listMedicines({ q: term, sort: "name", per_page: 8 }),
    enabled: term.length >= 2,
    staleTime: 60_000,
  });
  const rankOf = useMemo(() => new Map(ranked.map((r) => [r.medicine_id, r.rank])), [ranked]);
  const options: Medicine[] = term.length >= 2 ? (found.data?.data ?? []) : [];
  const showList = open && term.length >= 2;
  const current = Math.min(active, Math.max(0, options.length - 1));

  const pick = (m: Medicine) => {
    onPick(m.id);
    setText("");
    setOpen(false);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") return setOpen(false);
    if (!options.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActive((current + (e.key === "ArrowDown" ? 1 : options.length - 1)) % options.length);
    } else if (e.key === "Enter" && showList) {
      e.preventDefault();
      pick(options[current]);
    }
  };

  return (
    <section className="card p-4 sm:p-5" aria-labelledby={`${inputId}-label`}>
      <label id={`${inputId}-label`} htmlFor={inputId} className="label">
        Find a medicine's rank
      </label>
      <div className="relative">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="pointer-events-none absolute left-3.5 top-1/2 h-4.5 w-4.5 -translate-y-1/2 text-ink-muted" aria-hidden>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          id={inputId}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && options.length ? `${listId}-${current}` : undefined}
          autoComplete="off"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          placeholder="Type a medicine or brand name…"
          className="input pl-10"
        />
        {showList && (
          <ul id={listId} role="listbox" aria-label="Matching medicines" className="absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-line bg-surface py-1 shadow-raised">
            {found.isFetching && options.length === 0 ? (
              <li className="px-3 py-2 text-sm text-ink-muted">Searching…</li>
            ) : found.isError ? (
              <li className="px-3 py-2 text-sm text-danger">{describeError(found.error)}</li>
            ) : options.length === 0 ? (
              <li className="px-3 py-2 text-sm text-ink-muted">No medicine matches “{term}”.</li>
            ) : (
              options.map((m, i) => {
                const rank = rankOf.get(m.id);
                return (
                  <li
                    key={m.id}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === current}
                    onMouseDown={(e) => {
                      e.preventDefault(); // keep focus until picked
                      pick(m);
                    }}
                    onMouseEnter={() => setActive(i)}
                    className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm ${i === current ? "bg-brand-50" : ""}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-ink">{m.name}</span>
                      <span className="block truncate text-xs text-ink-muted">
                        {[m.brand, m.unit, m.active ? null : "disabled"].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs">
                      {rank !== undefined ? <span className="badge-brand">#{rank}</span> : <span className="text-ink-muted">not in top {RANKING_ROWS}</span>}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>
      <p className="field-hint">Searches the whole catalogue: a medicine outside the top {RANKING_ROWS}, or not sold at all, shows where it stands too.</p>
    </section>
  );
}

// ------------------------------------------------------------------ one medicine

function unitBars(m: MedicineSales, size = 1): ColumnBar[] {
  return chunkFromEnd(m.series, size).map((run) => {
    const starts = run.map((p) => p.start);
    const units = run.reduce((sum, p) => sum + p.units, 0);
    const revenue = run.reduce((sum, p) => sum + Number(p.revenue), 0);
    return {
      key: starts[0],
      label: m.series_bucket === "hour" ? wallClock(starts[0]).time : dayLabel(wallClock(starts[starts.length - 1]).date),
      title: runLabel(starts, m.series_bucket),
      value: units,
      valueText: formatNumber(units),
      details: [{ label: "revenue", value: formatINR(revenue) }],
    };
  });
}

function MedicineAnalytics({ id, days, sort, onClose }: { id: string; days: number; sort: MedicineSort; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["kpi", "medicine", id, days, sort],
    queryFn: () => medicineSales(id, days, sort),
    placeholderData: keepPreviousData,
  });
  const [asTable, setAsTable] = useState(false);
  const m = q.data;
  const titleId = useId();

  if (q.isLoading) {
    return (
      <div className="card space-y-4 p-5">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-52" />
      </div>
    );
  }
  if (!m) return <LoadError message={describeError(q.error)} onRetry={() => void q.refetch()} />;

  const vs = previousLabel(m.window_days);
  const p = m.previous;
  const unavailableShare = m.lines_requested ? m.lines_unavailable / m.lines_requested : 0;
  const bars = unitBars(m);
  return (
    <section className="card overflow-hidden" aria-labelledby={titleId} aria-busy={q.isPlaceholderData || undefined}>
      <header className="flex items-start justify-between gap-3 border-b border-line bg-raised px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <h3 id={titleId} className="truncate text-base font-semibold">
            {m.name}
          </h3>
          <p className="text-xs text-ink-muted">
            {[m.brand, m.unit, `MRP ${formatINR(m.mrp)}`].filter(Boolean).join(" · ")}
            {!m.active && <span className="badge-neutral ml-2 px-1.5 py-0 text-[10px]">Disabled</span>}
          </p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-ink-muted hover:bg-brand-50 hover:text-brand" aria-label="Close this medicine's analytics">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="h-5 w-5" aria-hidden>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      </header>

      <div className={`space-y-5 p-4 transition-opacity sm:p-5 ${q.isPlaceholderData ? "opacity-60" : ""}`}>
        <div className="grid gap-4 lg:grid-cols-[17rem_1fr]">
          <RankBlock m={m} vs={vs} />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Figure label="Units sold" value={formatNumber(m.units_sold)}>
              <ChangeBadge change={change(m.units_sold, p.units_sold)} vs={vs} />
            </Figure>
            <Figure label="Revenue" value={formatINRCompact(m.revenue)} exact={formatINR(m.revenue)}>
              <ChangeBadge change={change(Number(m.revenue), Number(p.revenue))} vs={vs} />
            </Figure>
            <Figure label="Orders" value={formatNumber(m.orders)}>
              <ChangeBadge change={change(m.orders, p.orders)} vs={vs} />
            </Figure>
            <Figure label="Customers" value={formatNumber(m.customers)}>
              <ChangeBadge change={change(m.customers, p.customers)} vs={vs} />
            </Figure>
          </div>
        </div>

        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-xl border border-line px-3 py-2.5">
            <dt className="text-xs text-ink-muted">Share of all sales</dt>
            <dd className="mt-0.5 text-ink">
              <strong className="font-semibold">{formatPercent(m.unit_share)}</strong> of units ·{" "}
              <strong className="font-semibold">{formatPercent(m.revenue_share)}</strong> of revenue
            </dd>
          </div>
          <div className="rounded-xl border border-line px-3 py-2.5">
            <dt className="text-xs text-ink-muted">Average price per unit</dt>
            <dd className="mt-0.5 text-ink">
              {m.average_unit_price === null ? (
                "—"
              ) : (
                <>
                  <strong className="font-semibold">{formatINR(m.average_unit_price)}</strong> before GST · MRP {formatINR(m.mrp)}
                </>
              )}
            </dd>
          </div>
          <div className="rounded-xl border border-line px-3 py-2.5">
            <dt className="text-xs text-ink-muted">Couldn't supply</dt>
            <dd className="mt-0.5 text-ink">
              {m.lines_requested === 0 ? (
                "Not in any order placed in the period"
              ) : m.lines_unavailable === 0 ? (
                `Supplied on all ${formatNumber(m.lines_requested)} orders asking for it`
              ) : (
                <span className="inline-flex items-center gap-1 font-medium text-warning">
                  <WarningIcon />
                  {formatNumber(m.lines_unavailable)} of {formatNumber(m.lines_requested)} orders ({formatPercent(unavailableShare)})
                </span>
              )}
            </dd>
          </div>
        </dl>

        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="section-title text-xs">Units sold per {m.series_bucket}</h4>
            <button type="button" onClick={() => setAsTable((t) => !t)} aria-pressed={asTable} className="btn-ghost btn-sm">
              {asTable ? "Show chart" : "Show table"}
            </button>
          </div>
          <ColumnChart
            bars={bars}
            seriesLabel="units"
            integer
            axisFormat={(v) => formatNumber(v)}
            asTable={asTable}
            height={180}
            summary={`${m.name}: units sold per ${m.series_bucket}, ${rangeLabel(m.period_start, m.period_end)}; ${formatNumber(m.units_sold)} in all.`}
            regroup={(maxBars) => unitBars(m, runLength(m.series.length, maxBars))}
          />
        </div>
      </div>
    </section>
  );
}

function RankBlock({ m, vs }: { m: MedicineSales; vs: string }) {
  const noun = sortNoun(m.sort);
  if (m.rank === null) {
    return (
      <div className="rounded-2xl border border-dashed border-line-strong bg-raised p-4">
        <div className="text-xs font-medium text-ink-muted">Rank by {noun}</div>
        <div className="mt-1 text-2xl font-semibold text-ink">Not sold</div>
        <p className="mt-1 text-sm text-ink-soft">
          No sales in this period
          {m.previous.rank !== null ? ` — it was #${formatNumber(m.previous.rank)} in ${vs}.` : `, nor in ${vs}.`}
        </p>
        <p className="mt-1 text-xs text-ink-muted">{formatNumber(m.ranked_medicines)} medicines sold in the period.</p>
      </div>
    );
  }
  const move = rankMove(m.rank, m.previous.rank);
  return (
    <div className="rounded-2xl bg-linear-to-br from-brand-50 to-surface p-4 ring-1 ring-brand-100">
      <div className="text-xs font-medium text-ink-muted">Rank by {noun}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-5xl font-semibold tracking-tight text-brand-900">#{formatNumber(m.rank)}</span>
        <RankMoveBadge rank={m.rank} previous={m.previous.rank} />
      </div>
      <p className="mt-1 text-sm text-ink-soft">
        of {formatNumber(m.ranked_medicines)} medicines sold · <strong className="font-semibold text-ink">{standing(m.rank, m.ranked_medicines)}</strong>
      </p>
      <p className="mt-1 text-xs text-ink-muted">
        {move.kind === "new"
          ? `Not sold in ${vs}.`
          : move.kind === "same"
            ? `Same rank as in ${vs}.`
            : `${move.kind === "up" ? "Up" : "Down"} ${move.by} from #${formatNumber(m.previous.rank ?? 0)} in ${vs}.`}
      </p>
    </div>
  );
}

function Figure({ label, value, exact, children }: { label: string; value: string; exact?: string; children?: ReactNode }) {
  return (
    <div className="min-w-0 rounded-xl border border-line p-3">
      <div className="truncate text-xs text-ink-muted">{label}</div>
      <div className="mt-0.5 truncate text-xl font-semibold text-ink" title={exact}>
        {value}
      </div>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ the ranking

const TH = "whitespace-nowrap px-2 pb-2 text-xs font-medium text-ink-muted first:pl-0 last:pr-0";

function RankingCard({
  query,
  sort,
  selected,
  onSelect,
}: {
  query: { data?: Ranking; isLoading: boolean; isPlaceholderData: boolean; error: unknown; refetch: () => unknown };
  sort: MedicineSort;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const d = query.data;
  const metric = (r: RankedMedicine) => (sort === "revenue" ? Number(r.revenue) : sort === "orders" ? r.orders : r.units_sold);
  const top = d?.rows[0] ? metric(d.rows[0]) : 0;
  // On a phone the ranking's own figure is the one column shown beside the name.
  const show = (col: MedicineSort) => (col === sort ? "" : col === "orders" ? "hidden md:table-cell" : "hidden sm:table-cell");

  return (
    <Card
      title={d ? `Top ${formatNumber(d.rows.length)} by ${sortNoun(sort)}` : `Top ${RANKING_ROWS}`}
      subtitle={
        d
          ? `${formatNumber(d.ranked_medicines)} medicines sold · ${formatNumber(d.total_units)} units · ${formatINRCompact(d.total_revenue)} in all`
          : undefined
      }
      busy={query.isPlaceholderData}
      action={
        <ExportButton
          table={`top medicines by ${sortNoun(sort)}`}
          columns={MEDICINE_RANK_EXPORT}
          rows={d?.rows ?? []}
          disabled={!d || query.isPlaceholderData}
        />
      }
    >
      {query.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 10 }, (_, i) => (
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      ) : !d ? (
        <LoadError message={describeError(query.error)} onRetry={() => void query.refetch()} />
      ) : d.rows.length === 0 ? (
        <p className="empty-state py-10">No medicines sold in this period.</p>
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 sm:-mx-5 sm:px-5">
          <table className="w-full min-w-[18rem] text-sm">
            <caption className="sr-only">
              Top {d.rows.length} medicines by {sortNoun(sort)}, with their moves since the previous period. Choose a medicine to see its analytics.
            </caption>
            <thead>
              <tr className="text-left">
                <th scope="col" className={TH}>#</th>
                <th scope="col" className={`${TH} w-full`}>Medicine</th>
                <th scope="col" className={`${TH} text-right ${show("units")}`}>Units</th>
                <th scope="col" className={`${TH} text-right ${show("orders")}`}>Orders</th>
                <th scope="col" className={`${TH} hidden text-right lg:table-cell`}>Customers</th>
                <th scope="col" className={`${TH} text-right ${show("revenue")}`}>Revenue</th>
                <th scope="col" className={`${TH} hidden w-32 lg:table-cell`}>
                  <span className="sr-only">Compared with the top seller</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {d.rows.map((r) => {
                const on = r.medicine_id === selected;
                return (
                  <tr key={r.medicine_id} className={on ? "bg-brand-50" : "hover:bg-raised"} aria-current={on || undefined}>
                    <td className="whitespace-nowrap py-2 pr-2">
                      <span className="flex items-center gap-1.5">
                        <span className="w-7 text-right font-semibold tabular-nums text-ink">{r.rank}</span>
                        <RankMoveBadge rank={r.rank} previous={r.previous_rank} />
                      </span>
                    </td>
                    <td className="max-w-0 px-2 py-2">
                      <button type="button" onClick={() => onSelect(r.medicine_id)} className="block w-full text-left">
                        <span className="block truncate font-medium text-ink hover:text-brand hover:underline">{r.name}</span>
                        <span className="block truncate text-xs text-ink-muted">{[r.brand, r.unit].filter(Boolean).join(" · ")}</span>
                      </button>
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${sort === "units" ? "font-semibold text-ink" : "text-ink-soft"} ${show("units")}`}>
                      {formatNumber(r.units_sold)}
                    </td>
                    <td className={`px-2 py-2 text-right tabular-nums ${sort === "orders" ? "font-semibold text-ink" : "text-ink-soft"} ${show("orders")}`}>
                      {formatNumber(r.orders)}
                    </td>
                    <td className="hidden px-2 py-2 text-right tabular-nums text-ink-soft lg:table-cell">{formatNumber(r.customers)}</td>
                    <td
                      className={`whitespace-nowrap px-2 py-2 text-right tabular-nums ${sort === "revenue" ? "font-semibold text-ink" : "text-ink-soft"} ${show("revenue")}`}
                      title={formatINR(r.revenue)}
                    >
                      {formatINRCompact(r.revenue)}
                    </td>
                    <td className="hidden py-2 pl-2 lg:table-cell">
                      {/* A set width: in this auto-width table the bar alone would shrink to nothing. */}
                      <div className="w-28">
                        <ShareBar
                          fraction={top ? metric(r) / top : 0}
                          label={`${formatPercent(sort === "revenue" ? r.revenue_share : r.unit_share)} of ${sort === "revenue" ? "revenue" : "units sold"}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
